import {
  useAllQueuesQuery,
  usePublicQueueQuery,
  useQueueByIdQuery,
  useQueueNamesQuery,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {usePublicQueueAccess} from '../hooks';
import {WANDB_ENTITY_NAME, WANDB_PUBLIC_QUEUE_NAME} from '../utils';
import {
  extractQueue,
  useExtractPublicQueues,
  useExtractQueues,
} from './queueUtil';

export const useQueueNames = (entityName: string) => {
  const query = useQueueNamesQuery({variables: {entityName}});

  type LiteQueue = {
    name: string;
    id: string;
  };
  const queues: LiteQueue[] = query.data ? query.data.project!.runQueues : [];

  return {
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
    queues,
  };
};

export const usePublicQueues = (skipQuery?: boolean) => {
  const {hasPublicQueueAccess, featureFlagQueryError, isFeatureFlagLoading} =
    usePublicQueueAccess();

  const queryPublicQueue = usePublicQueueQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      publicQueueName: WANDB_PUBLIC_QUEUE_NAME,
    },
    pollInterval: 30000,
    skip: !hasPublicQueueAccess || isFeatureFlagLoading || skipQuery,
    context: propagateErrorsContext(),
  });

  const publicQueues = useExtractPublicQueues(queryPublicQueue);

  return {
    loading: queryPublicQueue.loading || isFeatureFlagLoading,
    error: queryPublicQueue.error || featureFlagQueryError,
    refetch: queryPublicQueue.refetch,
    queues: publicQueues,
  };
};

/**
 * Return information about the queues in all entities.
 */
export const useQueues = () => {
  const viewer = useViewer();
  const personalEntity = viewer?.username ?? '';
  const entity = viewer?.entity;

  const queryAll = useAllQueuesQuery({
    pollInterval: 30000,
  });

  const skipPublicQueueQuery = entity === WANDB_ENTITY_NAME;
  const {
    queues: publicQueues,
    loading: publicQueuesLoading,
    error: publicQueuesError,
  } = usePublicQueues(skipPublicQueueQuery);

  const queues = useExtractQueues(queryAll, personalEntity);
  const allQueues = publicQueues ? [...publicQueues, ...queues] : queues;

  return {
    loading: queryAll.loading || publicQueuesLoading,
    error: queryAll.error || publicQueuesError,
    refetch: queryAll.refetch,
    queues: allQueues,
  };
};

export const useQueue = (entityName: string, queueId?: string) => {
  const {data, error, loading, refetch} = useQueueByIdQuery({
    variables: {entityName, queueId: queueId ?? ''},
    pollInterval: 5000,
    skip: !queueId,
  });

  if (loading || error) {
    return {error, loading, queue: undefined, refetch};
  }
  if (!data?.project?.runQueue) {
    // TODO: What is the correct way to specify error? GraphQLError?
    return {
      error: `No queue with id ${queueId} in entity ${entityName}`,
      loading: undefined,
      queue: undefined,
    };
  }
  const queue = extractQueue(entityName, data);

  return {
    queue,
    loading: undefined,
    error: undefined,
    refetch,
  };
};
